import {Box, P} from 'exsportia-components'
import {FC, useEffect, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'

import {
  applicableKey,
  cardAppliesTo,
  codeForApplicable,
  isApplicableApplied,
  pickBest,
} from '../../api/promo-codes/card-state'
import {getValidateErrorFromResponse} from '../../api/promo-codes/helpers'
import {useReadyPromos, useVenueOffers} from '../../api/promo-codes/my-promos'
import {ApplicableCopy, PromoApplied, PromoError, PromoValidateItem} from '../../api/promo-codes/types'
import {validatePromoCode} from '../../api/promo-codes/validate-promo-code.mutation'
import {theme} from '../../theme/theme'
import {useLocale} from '../../utils/i18n'
import {usePromoText} from './use-promo-text'

const ChipButton = styled.button<{isOn: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: start;
  min-width: 140px;
  max-width: 100%;
  padding: 8px 12px;
  border-radius: 12px;
  cursor: pointer;
  background: ${(props) => (props.isOn ? theme.colors['primary-secondary'] : theme.colors.bg)};
  border: 1.5px solid ${(props) => (props.isOn ? theme.colors.primary : theme.colors.border)};
  color: ${theme.colors['text-default']};

  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
`

const ChipTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
`

const ChipNote = styled.span`
  font-size: 12px;
  color: ${theme.colors['text-secondary']};
`

type Row = {
  key: string
  title: string
  note: string
  code: string | null
  on: boolean
  disabled: boolean
}

export type SavedCodeChipsProps = {
  venueId?: string | null
  items?: PromoValidateItem[]
  currency?: string | null
  applicable?: ApplicableCopy[]
  code?: string | null
  applied?: PromoApplied | null
  isQuoting?: boolean
  onApply: (code: string) => void
  onRemove: () => void
}

// "Use one of yours": the customer's saved codes for this venue as chips. With a quote the biggest
// saving is applied for them (once per promotion) and the ones that don't work show the server's
// reason; without one (store, memberships) only the cards that cover the items are offered.
export const SavedCodeChips: FC<SavedCodeChipsProps> = ({
  venueId,
  items,
  currency,
  applicable,
  code,
  applied = null,
  isQuoting = false,
  onApply,
  onRemove,
}) => {
  const {l} = useLocale()
  const {money, errorMessage} = usePromoText(currency)
  const ready = useReadyPromos(venueId ?? undefined)
  // The store and passes have no quote to price held cards on: the venue's public codes count too.
  const offers = useVenueOffers(applicable ? undefined : venueId ?? undefined)
  const [error, setError] = useState<PromoError | null>(null)
  const [isBusy, setIsBusy] = useState(false)
  const tried = useRef<string | null>(null)
  const codeRef = useRef(code)
  codeRef.current = code

  const rows: Row[] = useMemo(() => {
    if (applicable && applicable.length > 0) {
      return applicable.map((entry) => {
        const entryCode = codeForApplicable(entry, ready)
        return {
          key: applicableKey(entry),
          title: entry.title,
          note: entry.reason ?? l('promo_saves', {amount: money(entry.saves)}),
          code: entryCode,
          on: isApplicableApplied(entry, applied, ready),
          disabled: Boolean(entry.reason) || !entryCode,
        }
      })
    }
    if (applicable) {
      return []
    }
    const held = new Set(ready.map((card) => (card.code ?? '').replace(/\s+/g, '').toUpperCase()))
    const shared = offers.filter((card) => !held.has((card.code ?? '').replace(/\s+/g, '').toUpperCase()))
    return [...ready, ...shared]
      .filter((card) => card.code && (items ?? []).some((item) => cardAppliesTo(card, item)))
      .map((card) => {
        const cardCode = (card.code ?? '').replace(/\s+/g, '').toUpperCase()
        return {
          key: card.copyId || card.promoCodeId || cardCode,
          title: card.title,
          note: card.offerText,
          code: cardCode,
          on: Boolean(applied) && applied?.code.toUpperCase() === cardCode,
          disabled: false,
        }
      })
  }, [applicable, ready, offers, applied, items, money, l])

  const best = useMemo(() => (applicable ? pickBest(applicable, ready) : null), [applicable, ready])
  const bestCode = best?.code ?? null
  const bestKey = best?.key ?? null

  // Pick the biggest saving once per promotion; a code already typed or taken off stays as it is.
  useEffect(() => {
    if (!venueId || !bestCode || !bestKey || code || tried.current === bestKey) {
      return
    }
    tried.current = bestKey
    validatePromoCode({venueId, code: bestCode, items})
      .then(() => {
        if (!codeRef.current) {
          onApply(bestCode)
        }
      })
      .catch(() => undefined)
  }, [venueId, bestCode, bestKey, code])

  if (rows.length === 0) {
    return null
  }

  const noneWork = rows.every((row) => row.disabled)
  const showBest = rows.length > 1 && best && rows.some((row) => row.on && row.key === best.key)

  const choose = async (row: Row) => {
    if (row.on) {
      setError(null)
      onRemove()
      return
    }
    if (!row.code || !venueId) {
      return
    }
    setIsBusy(true)
    setError(null)
    try {
      await validatePromoCode({venueId, code: row.code, items})
      onApply(row.code)
    } catch (e) {
      setError(getValidateErrorFromResponse(e) || {code: 'FALLBACK'})
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <Box my='12px'>
      <P color='text-secondary'>{l('promo_your_promotions')}</P>
      {noneWork && <P color='text-secondary'>{l('promo_none_works')}</P>}
      {!noneWork && showBest && <P color='text-secondary'>{l('promo_picked_best')}</P>}
      <Box
        mt='6px'
        display='flex'
        flexWrap='wrap'
        gap='8px'
      >
        {rows.map((row) => (
          <ChipButton
            key={row.key}
            type='button'
            isOn={row.on}
            aria-pressed={row.on}
            disabled={row.disabled || isBusy || (isQuoting && !row.on)}
            onClick={() => void choose(row)}
          >
            <ChipTitle>{row.title}</ChipTitle>
            <ChipNote>{row.note}</ChipNote>
          </ChipButton>
        ))}
      </Box>
      {error && (
        <Box
          mt='6px'
          role='alert'
        >
          <P color='error'>{errorMessage(error)}</P>
        </Box>
      )}
    </Box>
  )
}
